/**
 * Category Store
 * Zustand store for category state management
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { CategoryListItem, CategoryDetailResponse } from '@/types/api/category';

/**
 * Category filter state
 */
export interface CategoryFilters {
  search: string;
  typeIds: number[];
  isActive: boolean | null;
}

/**
 * Category pagination state
 */
export interface CategoryPagination {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

/**
 * Category sort state
 */
export interface CategorySort {
  sortBy: number | null;
  sortOrder: 0 | 1; // 0=ASC, 1=DESC
}

/**
 * Category store state
 */
export interface CategoryState {
  // Data
  categories: CategoryListItem[];
  currentCategory: CategoryDetailResponse | null;

  // Pagination
  pagination: CategoryPagination;

  // Filters
  filters: CategoryFilters;

  // Sorting
  sort: CategorySort;

  // UI State
  isLoading: boolean;
  error: string | null;

  // Actions - Data
  setCategories: (categories: CategoryListItem[]) => void;
  setCurrentCategory: (category: CategoryDetailResponse | null) => void;
  addCategory: (category: CategoryListItem) => void;
  updateCategoryInList: (categoryId: number, updates: Partial<CategoryListItem>) => void;
  removeCategory: (categoryId: number) => void;

  // Actions - Pagination
  setPagination: (pagination: Partial<CategoryPagination>) => void;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;

  // Actions - Filters
  setFilters: (filters: Partial<CategoryFilters>) => void;
  setSearch: (search: string) => void;
  setTypeIds: (typeIds: number[]) => void;
  setIsActive: (isActive: boolean | null) => void;
  resetFilters: () => void;

  // Actions - Sorting
  setSort: (sort: Partial<CategorySort>) => void;
  setSortBy: (sortBy: number | null) => void;
  setSortOrder: (sortOrder: 0 | 1) => void;

  // Actions - UI State
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;

  // Actions - Reset
  reset: () => void;
}

/**
 * Initial state values
 */
const initialState = {
  categories: [],
  currentCategory: null,
  pagination: {
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  },
  filters: {
    search: '',
    typeIds: [],
    isActive: true, // Default to showing only active categories
  },
  sort: {
    sortBy: null,
    sortOrder: 0 as 0 | 1,
  },
  isLoading: false,
  error: null,
};

/**
 * Category Zustand store
 */
export const useCategoryStore = create<CategoryState>()(
  devtools(
    persist(
      (set) => ({
        ...initialState,

        // Data actions
        setCategories: (categories) =>
          set({ categories }, false, 'categories/setCategories'),

        setCurrentCategory: (category) =>
          set({ currentCategory: category }, false, 'categories/setCurrentCategory'),

        addCategory: (category) =>
          set(
            (state) => ({ categories: [category, ...state.categories] }),
            false,
            'categories/addCategory'
          ),

        updateCategoryInList: (categoryId, updates) =>
          set(
            (state) => ({
              categories: state.categories.map((c) =>
                c.categoryId === categoryId ? { ...c, ...updates } : c
              ),
            }),
            false,
            'categories/updateCategoryInList'
          ),

        removeCategory: (categoryId) =>
          set(
            (state) => ({
              categories: state.categories.filter((c) => c.categoryId !== categoryId),
            }),
            false,
            'categories/removeCategory'
          ),

        // Pagination actions
        setPagination: (pagination) =>
          set(
            (state) => ({
              pagination: { ...state.pagination, ...pagination },
            }),
            false,
            'categories/setPagination'
          ),

        setPage: (page) =>
          set(
            (state) => ({
              pagination: { ...state.pagination, page },
            }),
            false,
            'categories/setPage'
          ),

        setPageSize: (pageSize) =>
          set(
            (state) => ({
              pagination: { ...state.pagination, pageSize, page: 1 },
            }),
            false,
            'categories/setPageSize'
          ),

        // Filter actions
        setFilters: (filters) =>
          set(
            (state) => ({
              filters: { ...state.filters, ...filters },
              pagination: { ...state.pagination, page: 1 }, // Reset to page 1
            }),
            false,
            'categories/setFilters'
          ),

        setSearch: (search) =>
          set(
            (state) => ({
              filters: { ...state.filters, search },
              pagination: { ...state.pagination, page: 1 },
            }),
            false,
            'categories/setSearch'
          ),

        setTypeIds: (typeIds) =>
          set(
            (state) => ({
              filters: { ...state.filters, typeIds },
              pagination: { ...state.pagination, page: 1 },
            }),
            false,
            'categories/setTypeIds'
          ),

        setIsActive: (isActive) =>
          set(
            (state) => ({
              filters: { ...state.filters, isActive },
              pagination: { ...state.pagination, page: 1 },
            }),
            false,
            'categories/setIsActive'
          ),

        resetFilters: () =>
          set(
            {
              filters: initialState.filters,
              pagination: { ...initialState.pagination },
            },
            false,
            'categories/resetFilters'
          ),

        // Sort actions
        setSort: (sort) =>
          set(
            (state) => ({
              sort: { ...state.sort, ...sort },
            }),
            false,
            'categories/setSort'
          ),

        setSortBy: (sortBy) =>
          set(
            (state) => ({
              sort: { ...state.sort, sortBy },
            }),
            false,
            'categories/setSortBy'
          ),

        setSortOrder: (sortOrder) =>
          set(
            (state) => ({
              sort: { ...state.sort, sortOrder },
            }),
            false,
            'categories/setSortOrder'
          ),

        // UI state actions
        setLoading: (isLoading) =>
          set({ isLoading }, false, 'categories/setLoading'),

        setError: (error) =>
          set({ error }, false, 'categories/setError'),

        // Reset action
        reset: () =>
          set(initialState, false, 'categories/reset'),
      }),
      {
        name: 'category-storage',
        // Only persist filters, pagination settings, and sort
        partialize: (state) => ({
          filters: state.filters,
          pagination: {
            page: state.pagination.page,
            pageSize: state.pagination.pageSize,
          },
          sort: state.sort,
        }),
        // Merge persisted state with initial state, ensuring isActive defaults to true
        merge: (persistedState, currentState) => {
          const persisted = persistedState as Partial<CategoryState> | undefined;
          return {
            ...currentState,
            ...persisted,
            filters: {
              ...currentState.filters,
              ...persisted?.filters,
              // Always default isActive to true if it was null/undefined in storage
              isActive: persisted?.filters?.isActive ?? true,
            },
          };
        },
      }
    ),
    { name: 'CategoryStore' }
  )
);

/**
 * Selectors for common queries
 */

// Get filter query parameters for API
export const useCategoryQueryParams = () => {
  const { filters, pagination, sort } = useCategoryStore();

  // Convert boolean isActive to API format: 1=active, 2=inactive, 3=all
  let isActiveParam: number | undefined;
  if (filters.isActive === true) {
    isActiveParam = 1;
  } else if (filters.isActive === false) {
    isActiveParam = 2;
  } else {
    isActiveParam = 3; // null means all
  }

  return {
    page: pagination.page,
    pageSize: pagination.pageSize,
    search: filters.search || undefined,
    typeIds: filters.typeIds.length > 0
      ? filters.typeIds.join(',')
      : undefined,
    isActive: isActiveParam,
    sortBy: sort.sortBy ?? undefined,
    sortOrder: sort.sortOrder,
  };
};

// Check if filters are applied
export const useHasActiveCategoryFilters = () => {
  const { filters } = useCategoryStore();

  return (
    filters.search !== '' ||
    filters.typeIds.length > 0 ||
    filters.isActive !== null
  );
};
